import { useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { MAX_TASK_LINKS, type TaskLink } from "@shared/taskLinks";

/**
 * Search for companies and people to link a task to.
 * Spec: docs/superpowers/specs/2026-10-26-task-links-design.md
 *
 * One picker for quick-add, the task panel and Next step. Picking keeps the
 * search open, because the point is "Northridge AND Barnes": closing after
 * each pick would make every second link a second trip. A company result shows
 * its category and a person shows where they work, so two Acmes, or a Jane
 * Smith at each of two firms, can be told apart.
 */
interface SearchResults {
  companies: Array<{ id: string; name: string; category: string | null }>;
  contacts: Array<{ id: string; name: string; companyName: string | null }>;
}

export function RecordLinkPicker({
  value,
  onChange,
  children,
  exclude = [],
  disabled = false,
}: {
  value: TaskLink[];
  onChange: (next: TaskLink[]) => void;
  /** The popover trigger. */
  children: ReactNode;
  /** Records already linked another way, such as the record a Next step is
   *  written on: never offered, and counted toward the cap. */
  exclude?: Array<Pick<TaskLink, "kind" | "id">>;
  /** While the caller is saving: a pick made now would be built on a list the save is about to replace. */
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const full = value.length + exclude.length >= MAX_TASK_LINKS;

  const { data } = useQuery<SearchResults>({
    queryKey: ["/api/search", query],
    queryFn: async () => (await apiRequest("GET", `/api/search?q=${encodeURIComponent(query)}&limit=6`)).json(),
    enabled: open && !full && query.trim().length >= 2,
  });

  const taken = new Set([...value, ...exclude].map((l) => `${l.kind}:${l.id}`));
  const results: Array<TaskLink & { sub: string | null }> = [
    ...(data?.companies ?? []).map((c) => ({
      kind: "company" as const, id: c.id, name: c.name, category: c.category ?? null, companyName: null, sub: c.category ?? null,
    })),
    ...(data?.contacts ?? []).map((c) => ({
      kind: "contact" as const, id: c.id, name: c.name, category: null, companyName: c.companyName ?? null, sub: c.companyName ?? null,
    })),
  ].filter((r) => !taken.has(`${r.kind}:${r.id}`));

  const pick = (r: TaskLink & { sub: string | null }) => {
    if (disabled) return;
    const { sub: _sub, ...link } = r;
    onChange([...value, link]);
    setQuery("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start">
        {full ? (
          <p className="px-1 py-3 text-center text-xs text-muted-foreground">Up to {MAX_TASK_LINKS} records per task.</p>
        ) : (
          <>
            <div className="relative">
              <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search companies and contacts…"
                className="h-8 pl-7 text-xs"
              />
            </div>
            <div className="mt-2 max-h-56 overflow-y-auto">
              {query.trim().length < 2 ? (
                <p className="px-1 py-3 text-center text-xs text-muted-foreground">Type at least two characters.</p>
              ) : results.length === 0 ? (
                <p className="px-1 py-3 text-center text-xs text-muted-foreground">Nothing matches that.</p>
              ) : (
                results.map((r) => (
                  <button
                    key={`${r.kind}-${r.id}`}
                    type="button"
                    disabled={disabled}
                    onClick={() => pick(r)}
                    className="flex w-full items-baseline gap-2 rounded px-2 py-1.5 text-left text-xs hover:bg-muted disabled:cursor-wait disabled:opacity-50"
                  >
                    <span className="truncate font-medium">{r.name}</span>
                    {r.sub && <span className="truncate text-muted-foreground">{r.sub}</span>}
                  </button>
                ))
              )}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}
